import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../../store';
import {
  PrimeProcess,
  PrimeArtifactStateTransition,
  PrimeArtifactState,
} from '../../../types/primeModel';
import { PrimeArtifact } from '../../../types/primeArtifact';
import { ArtifactItemState } from '../../../types/artifactItems';
import { usePrimeArtifact } from '../../../store/hooks/usePrimeArtifact';
import { ProcessService } from '../../../services/api/processService';
import { ArtifactService } from '../../../services/api/artifactService';
import { TransitionService } from '../../../services/api/transitionService';
import TransitionCreationFlow from '../TransitionCreationFlow';
import './ProcessManager.css';

interface ProcessPageManagerProps {
  artifactId?: string;
}

type SearchType = 'process' | 'artifact' | 'state';

interface SearchResult {
  type: SearchType;
  items: Array<PrimeProcess | PrimeArtifact | PrimeArtifactState | ArtifactItemState>;
}

interface ProcessTransition extends PrimeArtifactStateTransition {
  artifact: PrimeArtifact;
  from_state_name: string;
  to_state_name: string;
}

interface ProcessWithTransitions extends PrimeProcess {
  transitions: Array<
    PrimeArtifactStateTransition & {
      artifact: PrimeArtifact;
      from_state_name: string;
      to_state_name: string;
    }
  >;
}

const ProcessPageManager: React.FC<ProcessPageManagerProps> = ({ artifactId }) => {
  const { fetchProcesses } = usePrimeArtifact();
  const [searchParams] = useSearchParams();

  const [searchType, setSearchType] = useState<SearchType>('process');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProcess, setSelectedProcess] = useState<PrimeProcess | null>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<PrimeArtifact | null>(null);
  const [transitions, setTransitions] = useState<ProcessTransition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [showTransitionCreation, setShowTransitionCreation] = useState(false);

  // Get processes and artifacts from Redux state
  const artifacts = useSelector((state: RootState) => state.primeArtifacts.primeArtifacts);

  const handleProcessSelectById = useCallback(async (processId: string) => {
    try {
      setLoading(true);
      console.log('[ProcessPageManager] Attempting to fetch Prime Process with ID:', processId);
      const process = await ProcessService.getById(processId);
      if (process) {
        void handleProcessSelect(process);
      }
    } catch (error) {
      console.error('Error fetching process by ID:', error);
      console.log(
        '[ProcessPageManager] This might be an Artifact Process ID being passed to Prime Process page'
      );
      setError(
        'Failed to load the selected process. This might be an Artifact Process - please navigate to the correct page.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (artifactId) {
      void fetchProcesses();
    }
  }, [artifactId, fetchProcesses]);

  // Handle processId query parameter to auto-select a process
  useEffect(() => {
    const processId = searchParams.get('processId');
    if (processId && !selectedProcess) {
      void handleProcessSelectById(processId);
    }
  }, [searchParams, selectedProcess, handleProcessSelectById]);

  const handleSearch = async (searchTerm: string) => {
    try {
      setLoading(true);
      const results: SearchResult = { type: searchType, items: [] };
      let artifacts;

      switch (searchType) {
        case 'process':
          results.items = await ProcessService.searchByName(searchTerm);
          break;
        case 'artifact':
          artifacts = await ArtifactService.search(searchTerm);
          results.items = artifacts.map((artifact) => ({
            id: artifact.id,
            name: artifact.name,
            description: artifact.description || undefined,
            artifact_type_id: artifact.artifact_type_id || 'unknown',
            artifact_type_name: '',
            parent_ref: artifact.parent?.id || undefined,
            parent_name: '',
            children: [],
            created_at: artifact.created_at,
            updated_at: artifact.updated_at || undefined,
            prime_artifact_id: artifact.prime_artifact_id || undefined,
          }));
          break;
        case 'state':
          if (selectedArtifact) {
            results.items = await ArtifactService.getStates(selectedArtifact.id);
          }
          break;
      }

      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to perform search');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessSelect = async (process: PrimeProcess) => {
    try {
      setLoading(true);
      const transitions = (await TransitionService.getTransitionsByProcess(
        process.id
      )) as unknown as PrimeArtifactStateTransition[];

      // Create a process with transitions
      const processWithTransitions: ProcessWithTransitions = {
        ...process,
        transitions: (await Promise.all(
          transitions.map(async (transition: PrimeArtifactStateTransition) => {
            const artifactData = await ArtifactService.getById(transition.prime_artifact_ref);
            const states = await ArtifactService.getStates(transition.prime_artifact_ref);

            const fromState = states.find(
              (s: ArtifactItemState) => s.id === transition.from_state_ref
            );
            const toState = states.find((s: ArtifactItemState) => s.id === transition.to_state_ref);

            if (!artifactData) {
              console.error('No artifact data available for transition');
              return null;
            }

            const artifact: PrimeArtifact = {
              id: artifactData.id,
              name: artifactData.name,
              description: artifactData.description || undefined,
              artifact_type_id: artifactData.artifact_type_id || 'unknown',
              artifact_type_name: '', // We'll need to fetch this separately
              parent_ref: artifactData.parent?.id || undefined,
              parent_name: '', // We'll need to fetch this separately
              children: [],
              created_at: artifactData.created_at,
              updated_at: artifactData.updated_at || undefined,
              prime_artifact_id: artifactData.prime_artifact_id || undefined,
            };

            return {
              ...transition,
              artifact,
              from_state_name: fromState?.name || '',
              to_state_name: toState?.name || '',
            };
          })
        )).filter((transition): transition is PrimeArtifactStateTransition & { artifact: PrimeArtifact; from_state_name: string; to_state_name: string; } => transition !== null),
      };

      setSelectedProcess(processWithTransitions);
    } catch (error) {
      console.error('Error fetching process details:', error);
      setError('Failed to fetch process details');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionCreate = async (newTransition: PrimeArtifactStateTransition) => {
    try {
      setLoading(true);

      // Fetch the artifact and states
      const artifact = await ArtifactService.getById(newTransition.prime_artifact_ref);
      const states = await ArtifactService.getStates(newTransition.prime_artifact_ref);

      // Find the state names from the states array
      const fromState = states.find(
        (state: ArtifactItemState) => state.id === newTransition.from_state_ref
      );
      const toState = states.find(
        (state: ArtifactItemState) => state.id === newTransition.to_state_ref
      );

      // Create a proper ProcessTransition object
      if (!artifact) {
        console.error('No artifact available for new transition');
        return;
      }

      const processTransition: ProcessTransition = {
        ...newTransition,
        artifact: {
          id: artifact.id,
          name: artifact.name,
          description: artifact.description || undefined,
          artifact_type_id: artifact.artifact_type_id || 'unknown',
          artifact_type_name: '', // We'll need to fetch this separately
          parent_ref: artifact.parent?.id || undefined,
          parent_name: '', // We'll need to fetch this separately
          children: [],
          created_at: artifact.created_at,
          updated_at: artifact.updated_at || undefined,
          prime_artifact_id: artifact.prime_artifact_id || undefined,
        },
        from_state_name: fromState?.name || '',
        to_state_name: toState?.name || '',
      };

      // Add the new transition to the list
      setTransitions((prev) => [...prev, processTransition]);

      // Hide the transition creation flow
      setShowTransitionCreation(false);
    } catch (err) {
      console.error('Error processing new transition:', err);
      setError('Failed to process the new transition. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const groupTransitionsByType = () => {
    const grouped = transitions.reduce(
      (acc, transition) => {
        const type = transition.default_transition_type;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(transition);
        return acc;
      },
      {} as Record<string, ProcessTransition[]>
    );

    return Object.entries(grouped).map(([type, transitions]) => ({
      type,
      transitions,
    }));
  };

  const renderSearchResults = () => {
    if (!searchResults) return null;

    return (
      <div className="search-results">
        <h3>Search Results</h3>
        <div className="results-list">
          {searchResults.items.map((item) => (
            <div
              key={item.id}
              className="result-item"
              onClick={() => {
                if (searchResults.type === 'process') {
                  void handleProcessSelect(item as PrimeProcess);
                } else if (searchResults.type === 'artifact') {
                  setSelectedArtifact(item as PrimeArtifact);
                }
              }}
            >
              <div className="result-name">{item.name}</div>
              {item.description && <div className="result-description">{item.description}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="process-manager">
      <div className="search-section">
        <div className="search-controls">
          <select value={searchType} onChange={(e) => setSearchType(e.target.value as SearchType)}>
            <option value="process">Process Name</option>
            <option value="artifact">Artifact Name</option>
            <option value="state">State Name</option>
          </select>

          {searchType === 'state' && (
            <select
              value={selectedArtifact?.id || ''}
              onChange={(e) => {
                const artifact = artifacts.find((a) => a.id === e.target.value);
                setSelectedArtifact(artifact || null);
              }}
            >
              <option value="">Select Artifact</option>
              {artifacts.map((artifact) => (
                <option key={artifact.id} value={artifact.id}>
                  {artifact.name}
                </option>
              ))}
            </select>
          )}

          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Search by ${searchType}...`}
          />

          <button onClick={() => handleSearch(searchTerm)} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {renderSearchResults()}

      {selectedProcess && (
        <div className="process-details">
          <div className="process-header">
            <h2>{selectedProcess.name}</h2>
            <p>{selectedProcess.description || 'No description available'}</p>
            <button
              className="add-transition-button"
              onClick={() => setShowTransitionCreation(true)}
            >
              Add New Transition
            </button>
          </div>

          {showTransitionCreation && (
            <TransitionCreationFlow
              process={selectedProcess}
              onTransitionCreated={handleTransitionCreate}
              onCancel={() => setShowTransitionCreation(false)}
              skipProcessSelection={true}
            />
          )}

          <div className="transitions-section">
            <h3>Transitions</h3>
            {groupTransitionsByType().map((group) => (
              <div key={group.type} className="transition-group">
                <h4>{group.type}</h4>
                <div className="transition-list">
                  {group.transitions.map((transition) => (
                    <div key={transition.id} className="transition-item">
                      <div className="transition-header">
                        <span className="artifact-name">{transition.artifact.name}</span>
                        <span className="transition-type">
                          {transition.default_transition_type}
                        </span>
                      </div>
                      <div className="transition-states">
                        <span className="from-state">{transition.from_state_name}</span>
                        <span className="arrow">→</span>
                        <span className="to-state">{transition.to_state_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessPageManager;
